import { cn } from '@/lib/utils';
import SectionHeader from '@/components/SectionHeader';
import { useIsMobile } from '@/hooks/use-mobile';
import { SimpleReveal } from '@/hooks/useSimpleAnimation';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { useHomeData } from '../../hooks/useHomeData';
import PatentCard from '../PatentCard';

export const HomePatentsSection = () => {
  const { patents } = useHomeData();
  const isMobile = useIsMobile();
  
  const {
    activeIndex,
    setActiveIndex,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isTransitioning,
    dragOffsetPercent,
    isDragging,
    containerRef
  } = useSwipeNavigation(patents.length);

  return (
    <section id="patented-technologies" className="overflow-visible relative z-10 section-spacing w-full bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleReveal>
          <div className="text-center mb-12">
            <SectionHeader title="Patented Technologies" subtitle="Innovation that delivers results" className="mb-6" />
            <p className="text-section-description max-w-4xl mx-auto text-center">
              Advanced lipid-based solutions that enhance drug delivery performance and support therapeutic success.
            </p>
          </div>
        </SimpleReveal>
        
        <SimpleReveal delay={300}>
          <div className="rounded-xl bg-white p-0 sm:p-4 overflow-visible">
            {/* Desktop and tablet: Static grid */}
            <div className="hidden sm:block">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {patents.map((patent, index) => (
                  <PatentCard
                    key={index}
                    icon={patent.icon}
                    title={patent.title}
                    description={patent.description}
                    callouts={patent.callouts}
                    alignment="left"
                    delay={index * 100}
                  />
                ))}
              </div>
            </div>

            {/* Mobile: Custom implementation with dots */}
            <div className="block sm:hidden">
              <div ref={containerRef} className="relative -mx-4 sm:mx-0 overflow-hidden">
                <div 
                  className={cn(
                    "flex transform-gpu touch-pan-y will-change-transform",
                    isDragging ? "transition-none" : "transition-transform duration-400 ease-[cubic-bezier(0.25,1,0.5,1)]"
                  )}
                  style={{ 
                    transform: `translate3d(calc(-${activeIndex * 100}% + ${dragOffsetPercent}%), 0, 0)`
                  }}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {patents.map((patent, index) => (
                    <div 
                      key={index} 
                      className="basis-full shrink-0 px-6 py-2"
                    >
                      <PatentCard
                        icon={patent.icon}
                        title={patent.title}
                        description={patent.description}
                        callouts={patent.callouts}
                        alignment="center"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Navigation Dots */}
              <div className="flex justify-center mt-8 space-x-3">
                {patents.map((patent, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    disabled={isTransitioning}
                    aria-pressed={index === activeIndex}
                    aria-label={`View technology ${index + 1} of ${patents.length}: ${patent.title}`}
                    className={`h-2 rounded-full transition-all duration-300 shadow-sm disabled:cursor-not-allowed ${
                      index === activeIndex ? 'bg-gradient-to-r from-[hsl(var(--color-1))] to-[hsl(var(--color-3))] w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </SimpleReveal>
      </div>
    </section>
  );
};
